import {Injectable, signal, computed, Signal, inject} from '@angular/core';
import { IItem } from '../../../types/IItem';
import {IPrice} from '../../../types/IPrice';
import {ResultsStore} from '../../../shared/store/results.store';
import {getSourceAvailabilityByType} from '../../../shared/utils/availability.utils';
import {AppStore} from '../../../shared/store/app.store';
import {EventService} from '../../../shared/services/event.service';
import {SettingsService} from '../../../shared/services/settings.service';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {combineLatest, debounceTime} from 'rxjs';
import {IColumnConfig} from '../../../types/IColumnConfig';

export interface ISortConfig {
  field: keyof IItem | 'quantity' | 'warranty' | null;
  direction: 'asc' | 'desc';
}

export interface IFilterConfig {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ResultTableService {
  private resultsStore = inject(ResultsStore);
  private appStore = inject(AppStore);
  private settings = inject(SettingsService);
  private eventService = inject(EventService);
  // State signals
  public sortConfig = signal<ISortConfig>({ field: 'availability', direction: 'asc' });
  public filterConfig = signal<IFilterConfig>({});
  isSidebarOpen = signal(!['xs', 'sm'].includes(this.appStore.currentBreakpoint()));

  readonly itemSize = computed(() => ['xs', 'sm'].includes(this.appStore.currentBreakpoint()) ? 255 : 56);
  // Modal state
  selectedItem = signal<IItem | null>(null);
  activeModal = signal<'details' | 'supplier' | null>(null);
  modalMode = signal<'compare' | 'quote' | null>(null);

  openDetailsModal(item: IItem): void {
    this.selectedItem.set(item);
    this.activeModal.set('details');
  }

  openSupplierModal(item: IItem, mode: 'compare' | 'quote'): void {
    this.selectedItem.set(item);
    this.modalMode.set(mode);
    this.activeModal.set('supplier');
  }

  closeModal(): void {
    this.activeModal.set(null);
    this.selectedItem.set(null);
  }

  addToCompare(item: IItem): void {
    this.openSupplierModal(item, 'compare');
  }

  addToQuote(item: IItem): void {
    this.openSupplierModal(item, 'quote');
  }

  removeFromCompare(item: IItem): void {
    this.resultsStore.removeCompareItem(item);
    this.closeModal();
  }

  handleSupplierSelected(item: IItem): void {
    if (this.modalMode() === 'compare') {
      this.resultsStore.addCompareItem(item);
    }
  }

  // Computed signals that automatically recalculate
  private filteredItems: Signal<IItem[]> = computed(() => {
    return this.applyFilters(this.resultsStore.results(), this.filterConfig());
  });

  // Full sorted+filtered list fed to virtual scroll
  public allRows: Signal<IItem[]> = computed(() => {
    return this.applySort(this.filteredItems(), this.sortConfig());
  });

  // Computed for total filtered items
  public totalFilteredItems: Signal<number> = computed(() => {
    return this.allRows().length;
  });

  setSort(field: keyof IItem|null, direction: 'asc' | 'desc'): void {
    this.sortConfig.set({ field, direction });
  }

  toggleSort(field: keyof IItem): void {
    const current = this.sortConfig();

    if(current.field === field && current.direction === 'desc') {
      return this.setSort(null, 'asc');
    }

    const direction = current.field === field && current.direction === 'asc' ? 'desc' : 'asc';
    this.setSort(field, direction);
  }

  setFilter(filterConfig: IFilterConfig): void {
    this.filterConfig.set(filterConfig);
  }

  updateFilter(key: string, value: any): void {
    const current = this.filterConfig();
    if (value.length || this.isRangeFilter(value)) {
      return this.setFilter({ ...current, [key]: value });
    }
    this.clearFilter(key);
  }

  clearFilters(): void {
    this.setFilter({});
  }

  clearFilter(key: string): void {
    const current = this.filterConfig();
    delete current[key];
    this.setFilter({ ...current });
  }

  toggleSidebar() {
    this.isSidebarOpen.update(v => !v);
  }

  private applySort(items: IItem[], sort: ISortConfig): IItem[] {
    if (!sort.field) {
      return items;
    }

    if(sort.field === 'availability') {
      return this.applyAvailabilitySort(items, sort);
    }

    const secondarySort = this.getSecondarySort(sort.field);

    return [...items].sort((a, b) => {
      const aVal = this.getSortValue(a, sort.field!);
      const bVal = this.getSortValue(b, sort.field!);

      if (aVal !== bVal) {
        // @ts-ignore
        const comparison = aVal > bVal ? 1 : -1;
        return sort.direction === 'asc' ? comparison : -comparison;
      }

      // Tiebreaker with secondary sort
      return this.compareSecondary(a, b, secondarySort);
    });
  }

  private getSortValue(item: IItem, field: keyof IItem | 'quantity' | 'warranty'): any {
    if (field === 'warranty') {
      return this.getWarrantyMiles(item);
    }

    if (field === 'quantity') {
      return undefined;
    }

    return item[field];
  }

  private getWarrantyMiles(item: IItem): number {
    if (!item.features?.mileage_warranty) {
      return 0;
    }

    return Number(item.features.mileage_warranty.replace('K', '')) * 1000;
  }

  private applyAvailabilitySort(items: IItem[], sort: ISortConfig): IItem[] {
    const sortedTypes = [...this.settings.get('availability_order').value]
      .sort((a, b) => a.order - b.order)
      .map(item => item.type);

    return [...items].sort((a, b) => {
      let matchedType: "local" | "transfer" | "distributor" | "external" | null = null;

      for (const type of sortedTypes) {
        const aHas = getSourceAvailabilityByType(a.availability, type) > 0;
        const bHas = getSourceAvailabilityByType(b.availability, type) > 0;

        if (aHas || bHas) {
          if (aHas === bHas) {
            matchedType = type;
            break;
          }
          const comparison = aHas ? -1 : 1;
          return sort.direction === 'asc' ? comparison : -comparison;
        }
      }

      return this.compareSecondary(a, b, this.getSecondarySort('availability'), matchedType);
    });
  }
  private getSecondarySort(primaryField: string): ISortConfig {
    const secondarySetting = this.settings.get('secondary_sort').value;

    if (secondarySetting.column === primaryField) {
      return { field: null, direction: 'asc' };
    }

    return { field: secondarySetting.column, direction: secondarySetting.direction};
  }

  private compareSecondary(a: IItem, b: IItem, secondarySort: ISortConfig, matchedType: "local" | "transfer" | "distributor" | "external" | null = null): number {
    if (!secondarySort.field) {
      return 0;
    }

    let aVal, bVal;

    if (secondarySort.field === 'quantity' && matchedType) {
      aVal = getSourceAvailabilityByType(a.availability, matchedType);
      bVal = getSourceAvailabilityByType(b.availability, matchedType);
    } else {
      aVal = this.getSortValue(a, secondarySort.field as keyof IItem | 'quantity' | 'warranty');
      bVal = this.getSortValue(b, secondarySort.field as keyof IItem | 'quantity' | 'warranty');
    }

    if (aVal === bVal) return 0;

    // @ts-ignore
    const comparison = aVal > bVal ? 1 : -1;
    return secondarySort.direction === 'asc' ? comparison : -comparison;
  }

  private applyFilters(items: IItem[], filter: IFilterConfig): IItem[] {
    if (Object.keys(filter).length === 0) {
      return items;
    }

    return items.filter(item => {
      return Object.entries(filter).every(([key, value]) => {
        if (this.isEmptyFilter(value)) {
          return true;
        }

        if (key === 'delivery') {
          return this.matchesDeliveryFilter(item, value);
        }

        if (key === 'specials') {
          return this.matchesSpecialsFilter(item, value);
        }

        if (key === 'features.mileage_warranty') {
          return this.matchesWarrantyFilter(item, value);
        }

        if (key === 'features.run_flat') {
          return this.matchesRunFlatFilter(item, value);
        }

        return this.matchesStandardFilter(item, key, value);
      });
    });
  }

  private isEmptyFilter(value: any): boolean {
    return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  }

  private matchesDeliveryFilter(item: IItem, filterValue: any): boolean {
    const localAvailable = getSourceAvailabilityByType(item.availability, 'local');

    if (filterValue.includes('On Hand') && localAvailable > 0) {
      return true;
    }

    if (filterValue.includes('Not Available') && localAvailable <= 0) {
      return true;
    }

    const deliveryNames = item.availability.map((source: any) => source.type === 'distributor' ? `${source.vendor.name} - ${source.name}` : source.name);

    return deliveryNames.length > 0 && deliveryNames.some(name => this.valueMatchesFilter(name, filterValue));
  }

  private matchesSpecialsFilter(item: IItem, filterValue: any): boolean {
    if (filterValue.includes('On Special')) {
      return this.isSpecial(this.resultsStore.getPricing()(item.id, item.cost));
    }

    if (filterValue.includes('Spiff')) {
      return item.spiff?.amount > 0;
    }

    if (filterValue.includes('Program')) {
      return !!(item?.programs && item.programs.length > 0);
    }

    if (filterValue.includes('Rebate')) {
      return !!(item?.rebates && item.rebates.length > 0);
    }

    return true;
  }

  private matchesWarrantyFilter(item: IItem, filterValue: any): boolean {
    let warranty = 0;
    if (item.features.mileage_warranty) {
      warranty = Number(item.features.mileage_warranty.replace('K', '')) * 1000;
    }

    return this.valueMatchesFilter(warranty, filterValue);
  }

  private matchesStandardFilter(item: IItem, key: string, filterValue: any): boolean {
    return this.valueMatchesFilter(this.getNestedValue(item, key), filterValue);
  }

  private getNestedValue(obj: any, path: string): any {
    let value: any = obj;
    for (const part of path.split('.')) {
      value = value?.[part];
    }
    return value;
  }

  private valueMatchesFilter(itemValue: any, filterValue: any): boolean {
    if (Array.isArray(filterValue)) {
      return filterValue.some(val => String(val).toLowerCase() === String(itemValue).toLowerCase());
    }

    if (this.isRangeFilter(filterValue)) {
      return itemValue >= filterValue.min && itemValue <= filterValue.max;
    }

    if (typeof filterValue === 'string') {
      return String(itemValue).toLowerCase().includes(filterValue.toLowerCase());
    }

    return itemValue === filterValue;
  }

  private isSpecial(price: IPrice) {
    return price?.price > price?.price_special;
  }

  private matchesRunFlatFilter(item: IItem, filterValue: any) {
    if (item.features?.run_flat === undefined) {
      return false;
    }

    return filterValue.includes(item.features?.run_flat === 'Y' ? 'Yes' : 'No');
  }

  private isRangeFilter(value: any): boolean {
    return typeof value === 'object' && value !== null && value.min !== undefined && value.max !== undefined;
  }

  get columns(): IColumnConfig[] {
    return [
      {data: 'image', header: 'Image', sortable: false,  classes: 'w-12'},
      {data: 'brand', header: 'Brand', sortable: true,  classes: 'w-24'},
      {data: 'model', header: 'Model', sortable: true,  classes: 'w-36'},
      {data: 'item_number', header: 'Item #', sortable: true,  classes: 'w-40'},
      {data: 'size', header: 'Size', sortable: true,  classes: 'w-32'},
      {data: 'price', header: this.resultsStore.currentPriceLevel()?.name || '', sortable: false, options: true,  classes: 'w-36'},
      {data: 'availability', header: 'Availability', sortable: false,  classes: 'w-44'},
      {data: 'warranty', header: 'Warranty', sortable: true,  classes: 'w-20'},
      {data: 'actions', header: 'Actions', sortable: false,  classes: 'justify-center w-28'},
    ];
  }
}
